import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client'
import { useHistory, useParams } from 'react-router-dom'
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  TextField, Switch, FormControlLabel, Select, MenuItem as MuiMenuItem,
  FormControl, InputLabel, Chip, Divider, Alert, CircularProgress,
  InputAdornment, IconButton, Tooltip, Autocomplete,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import { CATERING } from '../utils/catering'
import {
  createPromotion as createPromotionMutation,
  updatePromotion as updatePromotionMutation,
  publishPromotion as publishPromotionMutation,
  getPromotion,
  previewPromotion as previewPromotionQuery,
  getRestaurantDetail,
  getRestaurantProfile,
  restaurantStops,
  restaurantTrucks,
} from '../apollo'
import { GET_ADVANCED_MENUS } from '../apollo/advancedMenuQueries'
import PromotionHelp from './PromotionHelp'
import { promotionsTheme, PM, focusRing } from './promotionsTheme'

const CREATE_PROMOTION = gql`${createPromotionMutation}`
const UPDATE_PROMOTION = gql`${updatePromotionMutation}`
const PUBLISH_PROMOTION = gql`${publishPromotionMutation}`
const GET_PROMOTION = gql`${getPromotion}`
const PREVIEW_PROMOTION = gql`${previewPromotionQuery}`
const GET_RESTAURANT_DETAIL = gql`${getRestaurantDetail}`
const GET_RESTAURANT_PROFILE = gql`${getRestaurantProfile}`
const GET_STOPS = gql`${restaurantStops}`
const GET_TRUCKS = gql`${restaurantTrucks}`

const STEPS = ['Type', 'Trigger', 'Conditions', 'Reward', 'Limits', 'Preview', 'Publish']

const PROMO_TYPES = [
  { value: 'ORDER_AMOUNT_OFF', label: '$ Off Order', desc: 'e.g. Spend $70 get $10 off' },
  { value: 'ORDER_PERCENT_OFF', label: '% Off Order', desc: 'e.g. Spend $30 get 15% off' },
  { value: 'ITEM_PERCENT_OFF', label: '% Off Items', desc: 'e.g. 20% off all Burgers' },
  { value: 'ITEM_AMOUNT_OFF', label: '$ Off Items', desc: 'e.g. $2 off Large Fries' },
  { value: 'BOGO', label: 'Buy 1 Get 1 Free', desc: 'Cheapest eligible item free' },
  { value: 'BUY_X_GET_Y_PERCENT_OFF', label: 'Buy X Get Y% Off', desc: 'e.g. Buy 2 entrees, get appetizer 50% off' },
  { value: 'BUY_X_SAVE_AMOUNT', label: 'Buy X Save $', desc: 'e.g. Buy 2 combos save $5' },
  { value: 'FREE_ITEM_ON_THRESHOLD', label: 'Free Item', desc: 'e.g. Spend $50 get a free drink' },
  { value: 'FREE_DELIVERY_ON_THRESHOLD', label: 'Free Delivery', desc: 'e.g. Free delivery over $25' },
  { value: 'LOYALTY_REWARD', label: 'Loyalty Reward', desc: 'e.g. After $100 cumulative spend, get $10 off next order' },
]

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const emptyDraft = {
  promotionType: '',
  name: '',
  description: '',
  triggerType: 'AUTOMATIC',
  couponCode: '',
  targeting: 'ALL',
  channel: 'ALL',
  isCatering: false,
  isStackable: false,
  priority: 0,
  maxRedemptions: '',
  maxPerCustomer: '',
  maxDiscountAmount: '',
  startAtUtc: new Date().toISOString().slice(0, 16),
  endAtUtc: '',
  dayOfWeek: [],
  timeWindowStart: '',
  timeWindowEnd: '',
  // Food-truck stops the deal is limited to. Empty = every stop.
  stopIds: [],
  conditions: [],
  rewards: [],
}

// ─── Presentation ────────────────────────────────────────────────────────────

const PAGE = {
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }, bgcolor: PM.page, color: PM.text,
  fontFamily: PM.font, fontSize: 15, lineHeight: 1.45
}
const STEP_ROOT = { display: 'flex', flexDirection: 'column', gap: '22px' }
const STEP_HEADING = { fontSize: 22, fontWeight: 700, lineHeight: 1.3, color: PM.text }
const SUB_HEADING = { fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: PM.text }
const BODY_COPY = { fontSize: 15, lineHeight: 1.5, color: PM.body, '& strong': { color: PM.text } }
const HELPER = { fontSize: 13, lineHeight: 1.45, color: PM.secondary, '& strong': { color: PM.text } }
const CARD_TITLE = { fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: PM.text }
// Condition and reward cards.
const INNER_CARD = {
  p: '18px 12px 20px 20px', borderRadius: '14px', border: `1px solid ${PM.innerBorder}`,
  boxShadow: 'none', bgcolor: '#FFFFFF'
}
const INNER_CARD_HEAD = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5 }
const DANGER_ICON_BTN = {
  width: 44, height: 44, flexShrink: 0, borderRadius: '10px',
  bgcolor: PM.dangerBg, border: `1px solid ${PM.dangerBorder}`, color: PM.danger,
  '&:hover': { bgcolor: '#FDE4E4' },
  '& .MuiSvgIcon-root': { fontSize: 20, color: 'inherit' }
}
const DASHED_BTN = {
  height: 44, borderRadius: '10px', px: 2, textTransform: 'none',
  bgcolor: '#FFFFFF', color: PM.text, fontSize: 15, fontWeight: 600,
  border: `1px dashed ${PM.dashed}`,
  '&:hover': { bgcolor: PM.page, border: `1px dashed ${PM.text}` }
}
const SELECT_ALL_BTN = { textTransform: 'none', fontSize: 14, fontWeight: 700, px: 1, minWidth: 0 }
const fieldWidth = max => ({ width: '100%', maxWidth: max })
const TWO_COL = {
  display: 'grid', columnGap: 2, rowGap: 2.25, alignItems: 'start',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }
}
const FULL_ROW = { gridColumn: '1 / -1' }

// One clickable card on the Type and Trigger steps. The check in the corner
// carries the selected state as well as the colour.
function ChoiceCard({ selected, onClick, title, desc }) {
  return (
    <Box component="button" type="button" onClick={onClick} aria-pressed={selected}
      sx={{
        // Flex column so the title sits at the top when the grid stretches the card.
        position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start',
        width: '100%', boxSizing: 'border-box',
        textAlign: 'left', font: 'inherit', cursor: 'pointer', borderRadius: '14px',
        // 2px border when selected, so 1px less padding keeps the size.
        p: selected ? '17px 47px 17px 17px' : '18px 48px 18px 18px',
        border: selected ? `2px solid ${PM.text}` : `1px solid ${PM.innerBorder}`,
        bgcolor: selected ? PM.paleAmber : '#FFFFFF', color: PM.text,
        transition: 'border-color 150ms ease, background-color 150ms ease',
        '&:hover': selected ? {} : { borderColor: PM.inputBorder },
        '&:focus-visible': focusRing
      }}>
      <Typography component="span" sx={{ display: 'block', fontSize: 17, fontWeight: 700, lineHeight: 1.3, color: PM.text }}>{title}</Typography>
      <Typography component="span" sx={{ display: 'block', mt: 0.5, fontSize: 14, lineHeight: 1.45, color: selected ? PM.body : PM.secondary }}>{desc}</Typography>
      <Box component="span" aria-hidden sx={{
        position: 'absolute', top: selected ? 15 : 16, right: selected ? 15 : 16,
        width: 22, height: 22, boxSizing: 'border-box', borderRadius: '50%', display: 'grid', placeItems: 'center',
        ...(selected ? { bgcolor: PM.text, color: '#FFFFFF' } : { border: `1.5px solid ${PM.inputBorder}` })
      }}>
        {selected && <CheckRoundedIcon sx={{ fontSize: 15 }} />}
      </Box>
    </Box>
  )
}

// Stepper circle: done = ink with a check, current = amber with its number and
// a pale ring, upcoming = outlined number.
function StepDot({ active, completed, icon }) {
  return (
    <Box sx={{
      width: 36, height: 36, boxSizing: 'border-box', borderRadius: '50%', display: 'grid', placeItems: 'center',
      fontSize: 15, lineHeight: 1, fontVariantNumeric: 'tabular-nums',
      ...(completed
        ? { bgcolor: PM.text, color: '#FFFFFF' }
        : active
          ? { bgcolor: PM.amber, color: PM.text, fontWeight: 800, boxShadow: `0 0 0 5px ${PM.amberRing}` }
          : { bgcolor: '#FFFFFF', border: `1.5px solid ${PM.inputBorder}`, color: PM.secondary, fontWeight: 700 })
    }}>
      {completed ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : icon}
    </Box>
  )
}

const STEPPER = {
  '& .MuiStep-root': { px: 0.5 },
  '& .MuiStepConnector-root': {
    top: 17,
    left: { xs: 'calc(-50% + 22px)', sm: 'calc(-50% + 26px)' },
    right: { xs: 'calc(50% + 22px)', sm: 'calc(50% + 26px)' }
  },
  '& .MuiStepConnector-line': { borderTopWidth: 2, borderColor: PM.innerBorder },
  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
    mt: '10px', fontSize: { xs: 11, sm: 14 }, fontWeight: 500, lineHeight: 1.3, color: PM.secondary,
    whiteSpace: { xs: 'nowrap', sm: 'normal' }
  },
  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel.Mui-active': { fontWeight: 800, color: PM.text },
  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel.Mui-completed': { fontWeight: 500, color: PM.secondary }
}

// ─── Step components ─────────────────────────────────────────────────────────

function StepType({ draft, setDraft }) {
  return (
    <Box sx={STEP_ROOT}>
      <Typography variant="h6" sx={STEP_HEADING}>Choose a promotion type</Typography>
      <Box sx={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))' }}>
        {PROMO_TYPES.map(pt => (
          <ChoiceCard
            key={pt.value}
            onClick={() => setDraft(d => ({ ...d, promotionType: pt.value }))}
            selected={draft.promotionType === pt.value}
            title={pt.label}
            desc={pt.desc}
          />
        ))}
      </Box>
    </Box>
  )
}

function StepTrigger({ draft, setDraft }) {
  return (
    <Box sx={STEP_ROOT}>
      <Typography variant="h6" sx={STEP_HEADING}>How is this promotion triggered?</Typography>
      <Box sx={{ display: 'grid', gap: '14px', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
        {[
          { value: 'AUTOMATIC', label: 'Apply at Cart', desc: 'Customer sees this deal at checkout and can choose to apply it — no code required.' },
          { value: 'COUPON_CODE', label: 'Coupon Code', desc: 'Customer must enter a specific code to redeem.' },
        ].map(opt => (
          <ChoiceCard
            key={opt.value}
            onClick={() => setDraft(d => ({ ...d, triggerType: opt.value }))}
            selected={draft.triggerType === opt.value}
            title={opt.label}
            desc={opt.desc}
          />
        ))}
      </Box>

      {draft.triggerType === 'COUPON_CODE' && (
        <TextField
          label="Coupon Code"
          value={draft.couponCode}
          onChange={e => setDraft(d => ({ ...d, couponCode: e.target.value.toUpperCase() }))}
          size="small"
          sx={fieldWidth(300)}
          helperText="Customers will enter this code at checkout"
          inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 } }}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <TextField label="Promotion Name" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          fullWidth size="small" required />
        <TextField label="Description (optional)" value={draft.description}
          onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
          fullWidth size="small" multiline rows={2}
          // Visible textarea only: MUI measures row height with a hidden copy.
          sx={{ '& textarea:not([aria-hidden])': { minHeight: 72, resize: 'vertical' } }} />
        {/* One clickable row, like Stackable on the Limits step. */}
        <FormControlLabel
          labelPlacement="start"
          control={<Switch checked={!!draft.isCatering} onChange={e => setDraft(d => ({ ...d, isCatering: e.target.checked }))} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box aria-hidden sx={{
                width: 40, height: 40, borderRadius: '10px', flexShrink: 0, display: 'grid', placeItems: 'center',
                bgcolor: draft.isCatering ? '#FFFFFF' : CATERING.soft, color: CATERING.accent, border: `1px solid ${CATERING.border}`
              }}>
                <RoomServiceRoundedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 700, color: PM.text }}>Catering deal</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: 14, lineHeight: 1.45, color: draft.isCatering ? PM.body : PM.secondary }}>
                  Applies only to catering orders and shows the Catering deal badge.
                </Typography>
              </Box>
            </Box>
          }
          sx={{
            m: 0, width: '100%', boxSizing: 'border-box', justifyContent: 'space-between', gap: 2,
            borderRadius: '12px', p: '14px 16px', cursor: 'pointer',
            border: `1.5px solid ${draft.isCatering ? CATERING.accent : PM.btnBorder}`,
            bgcolor: draft.isCatering ? CATERING.soft : '#FFFFFF',
            transition: 'border-color 150ms ease, background-color 150ms ease',
            '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 }
          }}
        />
      </Box>
    </Box>
  )
}

function StepConditions({ draft, setDraft }) {
  const restaurantId = localStorage.getItem('restaurantId')
  const { data: menuData } = useQuery(GET_RESTAURANT_DETAIL, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-first',
  })
  const { data: advMenuData } = useQuery(GET_ADVANCED_MENUS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })
  const categories = menuData?.restaurant?.categories ?? []
  // Only use advanced menu items — the cart always sends advanced_menu_items IDs as foodId
  const allFoods = (advMenuData?.advancedMenus ?? []).flatMap(m =>
    (m.items || []).map(item => ({ id: String(item._id), label: `${item.name} (${item.categoryName || m.name})` }))
  )

  const addCondition = (type) => {
    const defaults = {
      SPEND_THRESHOLD: { minAmount: '' },
      REQUIRED_ITEMS: { itemIds: '', qty: 1 },
      REQUIRED_CATEGORY_POOL: { categoryIds: '', categoryLabels: '', categoryNames: '', qty: 2 },
      REQUIRED_QTY: { qty: 1 },
      DELIVERY_DISTANCE: { maxMiles: '' },
    }
    setDraft(d => ({ ...d, conditions: [...d.conditions, { conditionType: type, params: defaults[type] || {} }] }))
  }
  const removeCondition = (i) => setDraft(d => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }))
  const updateConditionParam = (i, key, val) => {
    setDraft(d => {
      const conds = [...d.conditions]
      conds[i] = { ...conds[i], params: { ...conds[i].params, [key]: val } }
      return { ...d, conditions: conds }
    })
  }

  const conditionLabels = {
    SPEND_THRESHOLD: 'Minimum Spend',
    REQUIRED_ITEMS: 'Required Items',
    REQUIRED_CATEGORY_POOL: 'Required Categories',
    REQUIRED_QTY: 'Minimum Quantity',
    DELIVERY_DISTANCE: 'Max Delivery Distance',
  }

  return (
    <Box sx={STEP_ROOT}>
      <Box>
        <Typography variant="h6" sx={STEP_HEADING}>Define eligibility conditions</Typography>
        <Typography variant="body2" sx={{ ...BODY_COPY, mt: 0.75 }}>All conditions must be met for the promotion to apply.</Typography>
      </Box>

      {draft.conditions.map((cond, i) => (
        <Paper key={i} elevation={0} sx={INNER_CARD}>
          <Box sx={INNER_CARD_HEAD}>
            <Typography variant="body2" sx={CARD_TITLE}>{conditionLabels[cond.conditionType] || cond.conditionType.replace(/_/g, ' ')}</Typography>
            <IconButton size="small" onClick={() => removeCondition(i)} sx={DANGER_ICON_BTN}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          {cond.conditionType === 'SPEND_THRESHOLD' && (
            <TextField label="Minimum Subtotal ($)" type="number" size="small"
              value={cond.params.minAmount} onChange={e => updateConditionParam(i, 'minAmount', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={fieldWidth(260)} />
          )}
          {cond.conditionType === 'REQUIRED_ITEMS' && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {allFoods.length > 0 ? (
                <Autocomplete
                  multiple
                  size="small"
                  options={allFoods}
                  getOptionLabel={o => o.label}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={allFoods.filter(f =>
                    (cond.params.itemIds || '').split(',').map(s => s.trim()).includes(String(f.id))
                  )}
                  onChange={(_, selected) => {
                    setDraft(d => {
                      const conds = [...d.conditions];
                      conds[i] = { ...conds[i], params: {
                        ...conds[i].params,
                        itemIds: selected.map(s => s.id).join(','),
                        itemNames: selected.map(s => s.label.split(' (')[0].toLowerCase()),
                      }};
                      return { ...d, conditions: conds };
                    });
                  }}
                  renderInput={params => <TextField {...params} label="Select menu items" />}
                  sx={{ flex: '1 1 260px', minWidth: 0 }}
                />
              ) : (
                <TextField label="Item IDs (comma-sep)" size="small" value={cond.params.itemIds}
                  onChange={e => updateConditionParam(i, 'itemIds', e.target.value)}
                  helperText="Item search requires a restaurant context"
                  sx={{ flex: '1 1 260px', maxWidth: 260 }} />
              )}
              <TextField label="Qty" type="number" size="small" value={cond.params.qty}
                onChange={e => updateConditionParam(i, 'qty', e.target.value)} sx={{ width: 100 }} />
            </Box>
          )}
          {cond.conditionType === 'REQUIRED_CATEGORY_POOL' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" sx={HELPER}>
                Customer must add <strong>{cond.params.qty}</strong> item(s) total from any of the selected categories combined.
              </Typography>
              {categories.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {(() => {
                      const allSelected = categories.every(c =>
                        (cond.params.categoryIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(c._id)
                      )
                      return (
                        <Button size="small" variant="text"
                          onClick={() => {
                            setDraft(d => {
                              const conds = [...d.conditions]
                              conds[i] = { ...conds[i], params: {
                                ...conds[i].params,
                                categoryIds: allSelected ? '' : categories.map(c => c._id).join(','),
                                categoryLabels: allSelected ? '' : categories.map(c => c.title).join(','),
                                categoryNames: allSelected ? '' : categories.map(c => c.title).join(', '),
                              }}
                              return { ...d, conditions: conds }
                            })
                          }}
                          sx={SELECT_ALL_BTN}>
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                      )
                    })()}
                  </Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={categories}
                    getOptionLabel={o => o.title}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={categories.filter(c =>
                      (cond.params.categoryIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(c._id)
                    )}
                    onChange={(_, selected) => {
                      setDraft(d => {
                        const conds = [...d.conditions]
                        conds[i] = { ...conds[i], params: {
                          ...conds[i].params,
                          categoryIds: selected.map(c => c._id).join(','),
                          categoryLabels: selected.map(c => c.title).join(','),
                          categoryNames: selected.map(c => c.title).join(', '),
                        }}
                        return { ...d, conditions: conds }
                      })
                    }}
                    renderInput={p => <TextField {...p} label="Select eligible categories" />}
                  />
                </>
              ) : (
                <TextField label="Category names (comma-separated)" size="small"
                  value={cond.params.categoryNames || ''}
                  onChange={e => updateConditionParam(i, 'categoryNames', e.target.value)}
                  helperText="e.g. Pizza, Pasta, Drinks"
                  sx={fieldWidth(260)} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField label="Required quantity from above" type="number" size="small"
                  value={cond.params.qty}
                  onChange={e => updateConditionParam(i, 'qty', e.target.value)}
                  sx={fieldWidth(260)}
                  inputProps={{ min: 1 }} />
              </Box>
            </Box>
          )}
          {cond.conditionType === 'REQUIRED_QTY' && (
            <TextField label="Minimum cart items" type="number" size="small"
              value={cond.params.qty} onChange={e => updateConditionParam(i, 'qty', e.target.value)} sx={fieldWidth(260)} />
          )}
          {cond.conditionType === 'DELIVERY_DISTANCE' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Miles"
                  type="number"
                  size="small"
                  value={cond.params.maxMiles}
                  onChange={e => updateConditionParam(i, 'maxMiles', e.target.value)}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  sx={fieldWidth(160)}
                />
                <Typography variant="body2" sx={{ ...BODY_COPY, pb: '13px' }}>miles from restaurant</Typography>
              </Box>
              <Typography variant="caption" sx={HELPER}>
                Offer applies only if customer is within this distance
              </Typography>
            </Box>
          )}
        </Paper>
      ))}

      <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {['SPEND_THRESHOLD', 'REQUIRED_ITEMS', 'REQUIRED_CATEGORY_POOL', 'REQUIRED_QTY', 'DELIVERY_DISTANCE']
          .filter(type => restaurantId || !['REQUIRED_ITEMS', 'REQUIRED_CATEGORY_POOL'].includes(type))
          .map(type => (
            <Button key={type} size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={() => addCondition(type)}
              sx={DASHED_BTN}>
              {conditionLabels[type] || type.replace(/_/g, ' ')}
            </Button>
          ))}
      </Box>

      <Divider sx={{ borderColor: PM.border }} />
      <Box>
      <Typography variant="subtitle2" sx={SUB_HEADING} mb={1.5}>Targeting & Channel</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ flex: '1 1 220px', maxWidth: 260 }}>
          <InputLabel>Customer Targeting</InputLabel>
          <Select label="Customer Targeting" value={draft.targeting}
            onChange={e => setDraft(d => ({ ...d, targeting: e.target.value }))}>
            {['ALL', 'NEW_CUSTOMERS', 'EXISTING_CUSTOMERS', 'LAPSED_CUSTOMERS'].map(v => (
              <MuiMenuItem key={v} value={v}>{v.replace(/_/g, ' ')}</MuiMenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 220px', maxWidth: 260 }}>
          <InputLabel>Channel</InputLabel>
          <Select label="Channel" value={draft.channel}
            onChange={e => setDraft(d => ({ ...d, channel: e.target.value }))}>
            {['ALL', 'DELIVERY', 'PICKUP'].map(v => (
              <MuiMenuItem key={v} value={v}>{v}</MuiMenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      </Box>
    </Box>
  )
}

function StepReward({ draft, setDraft }) {
  const restaurantId = localStorage.getItem('restaurantId')
  const { data: menuData } = useQuery(GET_RESTAURANT_DETAIL, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-first',
  })
  const { data: advMenuData } = useQuery(GET_ADVANCED_MENUS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  })
  const categories = menuData?.restaurant?.categories ?? []
  const allFoods = (advMenuData?.advancedMenus ?? []).flatMap(m =>
    (m.items || []).map(item => ({ id: String(item._id), label: `${item.name} (${item.categoryName || m.name})` }))
  )

  const addReward = (type) => {
    const defaults = {
      AMOUNT_OFF: { amount: '' },
      PERCENT_OFF: { percent: '' },
      FREE_ITEM: { foodId: '', title: '', maxQty: 1, freeMode: 'item', freeCategoryId: '', freeCategoryLabel: '' },
      CHEAPEST_ITEM_FREE: { eligibleCategoryId: '', eligibleItemIds: null },
      FREE_DELIVERY: {},
      GENERATE_VOUCHER: { voucherType: 'AMOUNT_OFF', voucherAmount: '', validDays: 30 },
    }
    setDraft(d => ({ ...d, rewards: [...d.rewards, { rewardType: type, params: defaults[type] || {} }] }))
  }
  const removeReward = (i) => setDraft(d => ({ ...d, rewards: d.rewards.filter((_, idx) => idx !== i) }))
  const updateRewardParam = (i, key, val) => {
    setDraft(d => {
      const rws = [...d.rewards]
      rws[i] = { ...rws[i], params: { ...rws[i].params, [key]: val } }
      return { ...d, rewards: rws }
    })
  }

  const rewardLabels = {
    AMOUNT_OFF: '$ Off',
    PERCENT_OFF: '% Off',
    FREE_ITEM: 'Free Item',
    CHEAPEST_ITEM_FREE: 'Buy 1 Get 1 Free',
    FREE_DELIVERY: 'Free Delivery',
    GENERATE_VOUCHER: 'Generate Voucher',
  }

  const suggestedRewards = {
    ORDER_AMOUNT_OFF: ['AMOUNT_OFF'],
    ORDER_PERCENT_OFF: ['PERCENT_OFF'],
    ITEM_PERCENT_OFF: ['PERCENT_OFF'],
    ITEM_AMOUNT_OFF: ['AMOUNT_OFF'],
    BOGO: ['CHEAPEST_ITEM_FREE'],
    BUY_X_GET_Y_PERCENT_OFF: ['PERCENT_OFF', 'FREE_ITEM'],
    BUY_X_SAVE_AMOUNT: ['AMOUNT_OFF', 'FREE_ITEM'],
    FREE_ITEM_ON_THRESHOLD: ['FREE_ITEM'],
    FREE_DELIVERY_ON_THRESHOLD: ['FREE_DELIVERY'],
    LOYALTY_REWARD: ['GENERATE_VOUCHER'],
  }
  const available = suggestedRewards[draft.promotionType] || Object.keys({ AMOUNT_OFF: 1, PERCENT_OFF: 1, FREE_ITEM: 1, CHEAPEST_ITEM_FREE: 1, FREE_DELIVERY: 1, GENERATE_VOUCHER: 1 })

  return (
    <Box sx={STEP_ROOT}>
      <Typography variant="h6" sx={STEP_HEADING}>Define the reward</Typography>

      {draft.rewards.map((rw, i) => (
        <Paper key={i} elevation={0} sx={INNER_CARD}>
          <Box sx={INNER_CARD_HEAD}>
            <Typography variant="body2" sx={CARD_TITLE}>{rewardLabels[rw.rewardType] || rw.rewardType.replace(/_/g, ' ')}</Typography>
            <IconButton size="small" onClick={() => removeReward(i)} sx={DANGER_ICON_BTN}><DeleteIcon fontSize="small" /></IconButton>
          </Box>
          {rw.rewardType === 'AMOUNT_OFF' && (
            <TextField label="Amount Off ($)" type="number" size="small"
              value={rw.params.amount} onChange={e => updateRewardParam(i, 'amount', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={fieldWidth(220)} />
          )}
          {rw.rewardType === 'PERCENT_OFF' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Percent Off" type="number" size="small"
                value={rw.params.percent} onChange={e => updateRewardParam(i, 'percent', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} sx={fieldWidth(220)} />
              {/* For BUY_X_GET_Y_PERCENT_OFF: pick which items get the discount */}
              {draft.promotionType === 'BUY_X_GET_Y_PERCENT_OFF' && (
                <>
                  {allFoods.length > 0 ? (
                    <Autocomplete
                      multiple
                      size="small"
                      options={allFoods}
                      getOptionLabel={o => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={allFoods.filter(f =>
                        (rw.params.eligibleItemIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(f.id)
                      )}
                      onChange={(_, selected) =>
                        updateRewardParam(i, 'eligibleItemIds', selected.map(s => s.id).join(',') || null)
                      }
                      renderInput={params => <TextField {...params} label="Items that get the discount (optional)" />}
                    />
                  ) : (
                    <TextField label="Item IDs to discount (comma-sep, optional)" size="small"
                      value={rw.params.eligibleItemIds || ''}
                      onChange={e => updateRewardParam(i, 'eligibleItemIds', e.target.value || null)}
                      helperText="Item search requires a restaurant context" />
                  )}
                  {categories.length > 0 ? (
                    <Autocomplete
                      size="small"
                      options={categories}
                      getOptionLabel={o => o.title}
                      isOptionEqualToValue={(o, v) => o._id === v._id}
                      value={categories.find(c => c._id === rw.params.eligibleCategoryId) || null}
                      onChange={(_, selected) => {
                        updateRewardParam(i, 'eligibleCategoryId', selected ? selected._id : '')
                        updateRewardParam(i, 'eligibleCategoryLabel', selected ? selected.title : '')
                      }}
                      renderInput={params => <TextField {...params} label="Or: category to discount (optional)" />}
                      sx={{ maxWidth: 320 }}
                    />
                  ) : null}
                </>
              )}
            </Box>
          )}
          {rw.rewardType === 'FREE_ITEM' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Toggle: specific item vs whole category */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {[{ value: 'item', label: 'Specific Item' }, { value: 'category', label: 'Any Item from Category' }].map(opt => (
                  <Button key={opt.value} size="small"
                    variant={(rw.params.freeMode || 'item') === opt.value ? 'contained' : 'outlined'}
                    onClick={() => updateRewardParam(i, 'freeMode', opt.value)}
                    sx={{ textTransform: 'none', borderRadius: '10px', fontSize: 14, fontWeight: 700 }}>
                    {opt.label}
                  </Button>
                ))}
              </Box>

              {(rw.params.freeMode !== 'category') && (
                /* Specific item mode */
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {allFoods.length > 0 ? (
                    <Autocomplete
                      size="small"
                      options={allFoods}
                      getOptionLabel={o => o.label}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={allFoods.find(f => f.id === rw.params.foodId) || null}
                      onChange={(_, selected) => {
                        updateRewardParam(i, 'foodId', selected ? selected.id : '')
                        updateRewardParam(i, 'title', selected ? selected.label.split(' (')[0] : '')
                      }}
                      renderInput={p => <TextField {...p} label="Select free item" />}
                      sx={{ flex: '1 1 220px', minWidth: 0 }}
                    />
                  ) : (
                    <>
                      <TextField label="Food ID" size="small" value={rw.params.foodId}
                        onChange={e => updateRewardParam(i, 'foodId', e.target.value)} sx={fieldWidth(220)} />
                      <TextField label="Item Name" size="small" value={rw.params.title}
                        onChange={e => updateRewardParam(i, 'title', e.target.value)} sx={{ flex: '1 1 220px', maxWidth: 220 }} />
                    </>
                  )}
                  <TextField label="Max Qty" type="number" size="small" value={rw.params.maxQty}
                    onChange={e => updateRewardParam(i, 'maxQty', e.target.value)} sx={{ width: 100 }} />
                </Box>
              )}

              {rw.params.freeMode === 'category' && (
                /* Category mode — customer can pick any item from these categories for free */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {categories.length > 0 ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {(() => {
                          const selectedIds = (rw.params.freeCategoryIds || '').split(',').map(s => s.trim()).filter(Boolean)
                          const allSelected = categories.length > 0 && categories.every(c => selectedIds.includes(c._id))
                          return (
                            <Button size="small" variant="text"
                              onClick={() => {
                                if (allSelected) {
                                  updateRewardParam(i, 'freeCategoryIds', '')
                                  updateRewardParam(i, 'freeCategoryLabels', '')
                                } else {
                                  updateRewardParam(i, 'freeCategoryIds', categories.map(c => c._id).join(','))
                                  updateRewardParam(i, 'freeCategoryLabels', categories.map(c => c.title).join(','))
                                }
                              }}
                              sx={SELECT_ALL_BTN}>
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </Button>
                          )
                        })()}
                      </Box>
                      <Autocomplete
                        multiple
                        size="small"
                        options={categories}
                        getOptionLabel={o => o.title}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        value={categories.filter(c =>
                          (rw.params.freeCategoryIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(c._id)
                        )}
                        onChange={(_, selected) => {
                          updateRewardParam(i, 'freeCategoryIds', selected.map(c => c._id).join(','))
                          updateRewardParam(i, 'freeCategoryLabels', selected.map(c => c.title).join(','))
                        }}
                        renderInput={p => <TextField {...p} label="Free item from categories" helperText="Customer picks any item from these categories for free" />}
                        sx={{ flex: '1 1 220px', minWidth: 0 }}
                      />
                    </>
                  ) : (
                    <TextField label="Category names (comma-sep)" size="small"
                      value={rw.params.freeCategoryLabels || ''}
                      onChange={e => updateRewardParam(i, 'freeCategoryLabels', e.target.value)}
                      helperText="e.g. Drinks, Sides" sx={fieldWidth(260)} />
                  )}
                  <TextField label="Max Qty" type="number" size="small" value={rw.params.maxQty}
                    onChange={e => updateRewardParam(i, 'maxQty', e.target.value)} sx={{ width: 100 }} />
                </Box>
              )}
            </Box>
          )}
          {rw.rewardType === 'CHEAPEST_ITEM_FREE' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" sx={HELPER}>
                Optionally restrict which items are eligible. Leave both blank to allow any item.
              </Typography>
              {/* Eligible Categories — multi-select */}
              {categories.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {(() => {
                      const selectedIds = (rw.params.eligibleCategoryIds || '').split(',').map(s => s.trim()).filter(Boolean)
                      const allSelected = categories.length > 0 && categories.every(c => selectedIds.includes(c._id))
                      return (
                        <Button size="small" variant="text"
                          onClick={() => {
                            if (allSelected) {
                              updateRewardParam(i, 'eligibleCategoryIds', '')
                              updateRewardParam(i, 'eligibleCategoryLabels', '')
                            } else {
                              updateRewardParam(i, 'eligibleCategoryIds', categories.map(c => c._id).join(','))
                              updateRewardParam(i, 'eligibleCategoryLabels', categories.map(c => c.title).join(','))
                            }
                          }}
                          sx={SELECT_ALL_BTN}>
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                      )
                    })()}
                  </Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={categories}
                    getOptionLabel={o => o.title}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={categories.filter(c =>
                      (rw.params.eligibleCategoryIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(c._id)
                    )}
                    onChange={(_, selected) => {
                      updateRewardParam(i, 'eligibleCategoryIds', selected.map(c => c._id).join(',') || '')
                      updateRewardParam(i, 'eligibleCategoryLabels', selected.map(c => c.title).join(',') || '')
                    }}
                    renderInput={params => <TextField {...params} label="Eligible Categories (optional)" />}
                  />
                </Box>
              ) : (
                <TextField label="Eligible Category IDs (comma-sep, optional)" size="small"
                  value={rw.params.eligibleCategoryIds || ''}
                  onChange={e => updateRewardParam(i, 'eligibleCategoryIds', e.target.value)}
                  helperText="Category search requires a restaurant context" />
              )}
              {/* Eligible Items — multi-select with Select All */}
              {allFoods.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {(() => {
                      const selectedIds = (rw.params.eligibleItemIds || '').split(',').map(s => s.trim()).filter(Boolean)
                      const allSelected = allFoods.length > 0 && allFoods.every(f => selectedIds.includes(f.id))
                      return (
                        <Button size="small" variant="text"
                          onClick={() => {
                            if (allSelected) {
                              updateRewardParam(i, 'eligibleItemIds', null)
                            } else {
                              updateRewardParam(i, 'eligibleItemIds', allFoods.map(f => f.id).join(','))
                            }
                          }}
                          sx={SELECT_ALL_BTN}>
                          {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                      )
                    })()}
                  </Box>
                  <Autocomplete
                    multiple
                    size="small"
                    options={allFoods}
                    getOptionLabel={o => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={allFoods.filter(f =>
                      (rw.params.eligibleItemIds || '').split(',').map(s => s.trim()).filter(Boolean).includes(f.id)
                    )}
                    onChange={(_, selected) =>
                      updateRewardParam(i, 'eligibleItemIds', selected.map(s => s.id).join(',') || null)
                    }
                    renderInput={params => <TextField {...params} label="Eligible Items (optional)" />}
                  />
                </Box>
              ) : (
                <TextField label="Eligible Item IDs (comma-sep, optional)" size="small"
                  value={rw.params.eligibleItemIds || ''}
                  onChange={e => updateRewardParam(i, 'eligibleItemIds', e.target.value || null)}
                  helperText="Item search requires a restaurant context" />
              )}
            </Box>
          )}
          {rw.rewardType === 'FREE_DELIVERY' && (
            <Typography variant="caption" sx={HELPER}>Delivery fee will be waived when conditions are met.</Typography>
          )}
          {rw.rewardType === 'GENERATE_VOUCHER' && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Voucher Amount ($)" type="number" size="small" value={rw.params.voucherAmount}
                onChange={e => updateRewardParam(i, 'voucherAmount', e.target.value)} sx={fieldWidth(220)} />
              <TextField label="Valid Days" type="number" size="small" value={rw.params.validDays}
                onChange={e => updateRewardParam(i, 'validDays', e.target.value)} sx={fieldWidth(220)} />
            </Box>
          )}
        </Paper>
      ))}

      <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {available.map(type => (
          <Button key={type} size="small" variant="outlined" startIcon={<AddIcon />}
            onClick={() => addReward(type)}
            sx={DASHED_BTN}>
            {rewardLabels[type] || type.replace(/_/g, ' ')}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

function StepLimits({ draft, setDraft, isTruck, stopOptions }) {
  const toggleDay = (d) => {
    const curr = draft.dayOfWeek || []
    setDraft(prev => ({
      ...prev,
      dayOfWeek: curr.includes(d) ? curr.filter(x => x !== d) : [...curr, d],
    }))
  }

  return (
    <Box sx={STEP_ROOT}>
      <Typography variant="h6" sx={STEP_HEADING}>Set limits & schedule</Typography>
      <Box sx={TWO_COL}>
        <Box>
          <TextField label="Start Date & Time" type="datetime-local" size="small" fullWidth
            value={draft.startAtUtc} onChange={e => setDraft(d => ({ ...d, startAtUtc: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Box>
        <Box>
          <TextField label="End Date & Time (optional)" type="datetime-local" size="small" fullWidth
            value={draft.endAtUtc} onChange={e => setDraft(d => ({ ...d, endAtUtc: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Box>
        <Box>
          <TextField label="Max Total Redemptions" type="number" size="small" fullWidth
            value={draft.maxRedemptions} onChange={e => setDraft(d => ({ ...d, maxRedemptions: e.target.value }))}
            helperText="Leave blank for unlimited" />
        </Box>
        <Box>
          <TextField label="Max Per Customer" type="number" size="small" fullWidth
            value={draft.maxPerCustomer} onChange={e => setDraft(d => ({ ...d, maxPerCustomer: e.target.value }))}
            helperText="Leave blank for unlimited" />
        </Box>
        <Box>
          <TextField label="Max Discount Cap ($)" type="number" size="small" fullWidth
            value={draft.maxDiscountAmount} onChange={e => setDraft(d => ({ ...d, maxDiscountAmount: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            helperText="Hard cap on discount value" />
        </Box>
        <Box>
          <TextField label="Priority (higher = wins)" type="number" size="small" fullWidth
            value={draft.priority} onChange={e => setDraft(d => ({ ...d, priority: parseInt(e.target.value, 10) || 0 }))} />
        </Box>
        <Box sx={FULL_ROW}>
          {/* One clickable row: the label is the FormControlLabel, so a click anywhere toggles. */}
          <FormControlLabel
            labelPlacement="start"
            control={<Switch checked={draft.isStackable} onChange={e => setDraft(d => ({ ...d, isStackable: e.target.checked }))} />}
            label={<Box><Typography variant="body2" sx={{ fontSize: 15, fontWeight: 700, color: PM.text }}>Stackable</Typography><Typography variant="caption" sx={{ display: 'block', fontSize: 14, lineHeight: 1.45, color: draft.isStackable ? PM.body : PM.secondary }}>Can combine with other promotions</Typography></Box>}
            sx={{
              m: 0, width: '100%', boxSizing: 'border-box', justifyContent: 'space-between', gap: 2,
              borderRadius: '12px', p: '14px 16px', cursor: 'pointer',
              border: `1.5px solid ${draft.isStackable ? PM.text : PM.btnBorder}`,
              bgcolor: draft.isStackable ? PM.paleAmber : '#FFFFFF',
              transition: 'border-color 150ms ease, background-color 150ms ease',
              '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 }
            }}
          />
        </Box>
        <Box sx={FULL_ROW}>
          <Typography variant="body2" sx={{ ...SUB_HEADING, display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            Active Days (optional)
            <Tooltip title="Leave all deselected to apply every day"><InfoOutlinedIcon sx={{ fontSize: 18, color: PM.secondary }} /></Tooltip>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {DAYS.map((day, idx) => (
              <Chip key={idx} label={day} size="small" clickable
                onClick={() => toggleDay(idx)}
                sx={{
                  height: 44, minWidth: 58, borderRadius: '999px', fontSize: 15,
                  fontWeight: (draft.dayOfWeek || []).includes(idx) ? 700 : 600,
                  bgcolor: (draft.dayOfWeek || []).includes(idx) ? PM.text : '#FFFFFF',
                  color: (draft.dayOfWeek || []).includes(idx) ? '#FFFFFF' : PM.body,
                  border: `1.5px solid ${(draft.dayOfWeek || []).includes(idx) ? PM.text : PM.innerBorder}`,
                  '&:hover': { bgcolor: (draft.dayOfWeek || []).includes(idx) ? PM.primaryHover : PM.page },
                  '&.Mui-focusVisible': { ...focusRing, bgcolor: (draft.dayOfWeek || []).includes(idx) ? PM.text : '#FFFFFF' },
                  '& .MuiChip-label': { px: 2 }
                }} />
            ))}
          </Box>
        </Box>
        <Box>
          <TextField label="Time Window Start (optional)" type="time" size="small" fullWidth
            value={draft.timeWindowStart} onChange={e => setDraft(d => ({ ...d, timeWindowStart: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Box>
        <Box>
          <TextField label="Time Window End (optional)" type="time" size="small" fullWidth
            value={draft.timeWindowEnd} onChange={e => setDraft(d => ({ ...d, timeWindowEnd: e.target.value }))}
            InputLabelProps={{ shrink: true }} />
        </Box>
        {/* Food trucks: a deal can run at some stops only. Empty = every stop. */}
        {isTruck && (
          <Box sx={FULL_ROW}>
            <Autocomplete
              multiple
              size="small"
              options={stopOptions}
              getOptionLabel={o => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={stopOptions.filter(o => (draft.stopIds || []).includes(o.id))}
              onChange={(_, selected) => setDraft(d => ({ ...d, stopIds: selected.map(s => s.id) }))}
              noOptionsText="No stops on the schedule yet"
              renderOption={(props, o) => {
                const { key, ...rest } = props
                return (
                  <li key={key || o.id} {...rest}>
                    <Box sx={{ minWidth: 0, py: 0.5 }}>
                      <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: PM.text, overflowWrap: 'anywhere' }}>{o.label}</Typography>
                      {o.detail ? (
                        <Typography component="span" sx={{ display: 'block', fontSize: 13, lineHeight: 1.4, color: PM.secondary, overflowWrap: 'anywhere' }}>{o.detail}</Typography>
                      ) : null}
                    </Box>
                  </li>
                )
              }}
              renderInput={p => (
                <TextField {...p} label="Only at these stops (optional)"
                  placeholder={(draft.stopIds || []).length ? '' : 'Every stop'}
                  helperText="Leave it empty and the deal runs at every stop." />
              )}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const EMPTY_CART_ITEM = { title: '', categoryName: '', unitPrice: '', quantity: '1' }

function StepPreview({ draft, promotionId }) {
  const restaurantId = localStorage.getItem('restaurantId') || null
  const { data: menuData } = useQuery(GET_RESTAURANT_DETAIL, {
    variables: { id: restaurantId }, skip: !restaurantId, fetchPolicy: 'cache-first',
  })
  const { data: advMenuData } = useQuery(GET_ADVANCED_MENUS, {
    variables: { restaurantId }, skip: !restaurantId, fetchPolicy: 'cache-and-network',
  })
  const categories = menuData?.restaurant?.categories ?? []
  const allFoods = (advMenuData?.advancedMenus ?? []).flatMap(m =>
    (m.items || []).map(item => ({
      id: String(item._id),
      label: `${item.name} (${item.categoryName || m.name})`,
      name: item.name,
      categoryName: item.categoryName || m.name,
      price: item.basePrice || '',
    }))
  )

  const [cartInput, setCartInput] = useState({ deliveryFee: '', promoCode: '' })
  const [cartItems, setCartItems] = useState([{ ...EMPTY_CART_ITEM }])
  const [previewResult, setPreviewResult] = useState(null)

  const addItem = () => setCartItems(ci => [...ci, { ...EMPTY_CART_ITEM }])
  const removeItem = idx => setCartItems(ci => ci.filter((_, i) => i !== idx))
  const updateItem = (idx, field, val) => setCartItems(ci => ci.map((item, i) => i === idx ? { ...item, [field]: val } : item))

  const [runPreview, { loading }] = useLazyQuery(PREVIEW_PROMOTION, {
    onCompleted: data => setPreviewResult(data?.previewPromotion),
  })

  const handlePreview = () => {
    if (!promotionId) return
    const builtItems = cartItems
      .filter(ci => ci.title || ci.unitPrice)
      .map((ci, idx) => {
        const matchedFood = allFoods.find(f => f.name.toLowerCase() === ci.title.toLowerCase())
        const matchedCat = categories.find(c => c.title.toLowerCase() === (ci.categoryName || '').toLowerCase())
        return {
          foodId: matchedFood ? matchedFood.id : String(idx + 1),
          title: ci.title || `Item ${idx + 1}`,
          categoryId: matchedCat ? matchedCat._id : undefined,
          categoryName: ci.categoryName || undefined,
          unitPrice: parseFloat(ci.unitPrice) || 0,
          quantity: parseInt(ci.quantity) || 1,
        }
      })
    const subtotal = builtItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0)
    runPreview({
      variables: {
        promotionId,
        cartInput: {
          restaurantId: restaurantId || null,
          channel: draft.channel === 'PICKUP' ? 'PICKUP' : 'DELIVERY',
          cartItems: builtItems,
          subtotal,
          deliveryFee: parseFloat(cartInput.deliveryFee) || 0,
          promoCode: cartInput.promoCode || draft.couponCode || null,
        },
      },
    })
  }

  return (
    <Box sx={STEP_ROOT}>
      <Box>
        <Typography variant="h6" sx={STEP_HEADING}>Preview this promotion</Typography>
        <Typography variant="body2" sx={{ ...BODY_COPY, mt: 0.75 }}>
          Add items to simulate a real cart and see exactly what discount would apply.
          {!promotionId && <strong> Save as draft first to enable preview.</strong>}
        </Typography>
      </Box>

      {/* The simulator: one tinted panel */}
      <Box sx={{ bgcolor: PM.page, borderRadius: '14px', p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Cart items builder */}
        <Typography variant="subtitle2" sx={SUB_HEADING}>Cart Items</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {cartItems.map((ci, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {restaurantId && (allFoods.length > 0 ? (
                <Autocomplete
                  size="small"
                  freeSolo
                  options={allFoods}
                  getOptionLabel={o => typeof o === 'string' ? o : o.label.split(' (')[0]}
                  value={allFoods.find(f => f.label.split(' (')[0] === ci.title) || ci.title}
                  onInputChange={(_, val) => {
                    const matched = allFoods.find(f => f.label.split(' (')[0] === val)
                    updateItem(idx, 'title', val)
                    if (matched) {
                      const matchedCat = categories.find(c => c._id === matched.categoryId)
                      if (matchedCat) updateItem(idx, 'categoryName', matchedCat.title)
                      if (matched.price) updateItem(idx, 'unitPrice', String(matched.price))
                    }
                  }}
                  renderInput={p => <TextField {...p} label="Item name" />}
                  sx={{ flex: '1 1 180px', minWidth: 160 }}
                />
              ) : (
                <TextField label="Item name" size="small" value={ci.title}
                  onChange={e => updateItem(idx, 'title', e.target.value)} sx={{ flex: '1 1 180px', minWidth: 160 }} />
              ))}
              {restaurantId && (categories.length > 0 ? (
                <Autocomplete
                  size="small"
                  freeSolo
                  options={categories}
                  getOptionLabel={o => typeof o === 'string' ? o : o.title}
                  value={categories.find(c => c.title === ci.categoryName) || ci.categoryName}
                  onInputChange={(_, val) => updateItem(idx, 'categoryName', val)}
                  renderInput={p => <TextField {...p} label="Category" />}
                  sx={{ flex: '1 1 180px', minWidth: 140 }}
                />
              ) : (
                <TextField label="Category" size="small" value={ci.categoryName}
                  onChange={e => updateItem(idx, 'categoryName', e.target.value)} sx={{ flex: '1 1 180px', minWidth: 140 }} />
              ))}
              <TextField label="Price ($)" type="number" size="small" value={ci.unitPrice}
                onChange={e => updateItem(idx, 'unitPrice', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                sx={{ width: 110 }} />
              <TextField label="Qty" type="number" size="small" value={ci.quantity}
                onChange={e => updateItem(idx, 'quantity', e.target.value)} sx={{ width: 80 }} />
              {cartItems.length > 1 && (
                <IconButton size="small" onClick={() => removeItem(idx)} sx={DANGER_ICON_BTN}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button size="small" variant="text" onClick={addItem}
            sx={{ textTransform: 'none', alignSelf: 'flex-start', fontSize: 15, fontWeight: 700, px: 1.25 }}>
            + Add another item
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 220px', maxWidth: 280 }}>
            <TextField label="Delivery Fee ($)" type="number" size="small" fullWidth
              value={cartInput.deliveryFee} onChange={e => setCartInput(c => ({ ...c, deliveryFee: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
          </Box>
          {draft.triggerType === 'COUPON_CODE' && (
            <Box sx={{ flex: '1 1 220px', maxWidth: 280 }}>
              <TextField label="Promo Code" size="small" fullWidth
                value={cartInput.promoCode} onChange={e => setCartInput(c => ({ ...c, promoCode: e.target.value.toUpperCase() }))}
                inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 2 } }} />
            </Box>
          )}
        </Box>
        <Button variant="contained" onClick={handlePreview} disabled={!promotionId || loading}
          sx={{
            alignSelf: 'flex-start', height: 46, borderRadius: '10px', textTransform: 'none', fontWeight: 700,
            bgcolor: PM.amber, color: PM.text, '&:hover': { bgcolor: PM.amberHover }
          }}>
          {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Simulate Cart
        </Button>

        {previewResult && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '14px', border: `1px solid ${PM.border}`, boxShadow: 'none', bgcolor: '#FFFFFF' }}>
            {previewResult.discountAmount > 0 ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: PM.amberIcon }} />
                  <Typography sx={{ fontSize: 17, fontWeight: 700, color: PM.text }}>Promotion would apply!</Typography>
                </Box>
                <Typography variant="body2" sx={BODY_COPY}>{previewResult.explanationMessage}</Typography>
                <Divider sx={{ my: 2, borderColor: PM.rowBorder }} />
                {[
                  ['Original Subtotal', `$${previewResult.originalSubtotal?.toFixed(2)}`],
                  ['Discount', `-$${previewResult.discountAmount?.toFixed(2)}`],
                  ['Delivery Fee', `$${previewResult.discountedDeliveryFee?.toFixed(2)}`],
                  ['Final Total', `$${previewResult.finalTotal?.toFixed(2)}`],
                ].map(([label, val]) => (
                  <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: 15, color: PM.secondary }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontSize: 15, color: PM.text, fontVariantNumeric: 'tabular-nums', fontWeight: label === 'Final Total' ? 700 : 500 }}>{val}</Typography>
                  </Box>
                ))}
              </>
            ) : (
              <Alert severity="warning">Promotion would NOT apply with this cart. Check conditions.</Alert>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  )
}

function StepPublish({ draft, isTruck, stopOptions }) {
  const promoTypeLabel = PROMO_TYPES.find(p => p.value === draft.promotionType)?.label || draft.promotionType?.replace(/_/g, ' ') || '—'
  const ptLabel = (v) => v?.replace(/_/g, ' ') || '—'
  const stopNames = (draft.stopIds || []).map(id => stopOptions.find(o => o.id === id)?.label || 'A removed stop')
  const rows = [
    ['Type', promoTypeLabel],
    ['Name', draft.name],
    ['Trigger', draft.triggerType === 'COUPON_CODE' ? `Code: ${draft.couponCode}` : 'Apply at Cart'],
    ['Targeting', ptLabel(draft.targeting)],
    ['Channel', draft.channel],
    ...(isTruck ? [['Stops', stopNames.length ? stopNames.join(', ') : 'Every stop']] : []),
    ...(draft.isCatering ? [['Catering deal', 'Yes']] : []),
    ['Start', draft.startAtUtc ? new Date(draft.startAtUtc).toLocaleString() : '—'],
    ['End', draft.endAtUtc ? new Date(draft.endAtUtc).toLocaleString() : 'Never'],
    ['Max Redemptions', draft.maxRedemptions || 'Unlimited'],
    ['Per Customer', draft.maxPerCustomer || 'Unlimited'],
    ['Stackable', draft.isStackable ? 'Yes' : 'No'],
    ['Conditions', `${draft.conditions.length} condition(s)`],
    ['Rewards', `${draft.rewards.length} reward(s)`],
  ]
  return (
    <Box sx={STEP_ROOT}>
      <Typography variant="h6" sx={STEP_HEADING}>Review & Publish</Typography>
      <Paper elevation={0} sx={{ borderRadius: '14px', border: `1px solid ${PM.innerBorder}`, boxShadow: 'none', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
        {rows.map(([label, val]) => (
          <Box key={label} sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, px: '20px', py: '13px',
            '&:not(:last-of-type)': { borderBottom: `1px solid ${PM.rowBorder}` }
          }}>
            <Typography variant="body2" sx={{ fontSize: 15, color: PM.secondary, width: 160, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" textAlign="right" sx={{ fontSize: 15, fontWeight: 700, color: PM.text, minWidth: 0, overflowWrap: 'anywhere' }}>{val}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  )
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

function PromotionWizard() {
  const history = useHistory()
  const { id } = useParams()
  const restaurantId = localStorage.getItem('restaurantId') || null
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState(emptyDraft)
  const [savedId, setSavedId] = useState(id || null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Load existing promotion for edit
  const { loading: loadingPromo } = useQuery(GET_PROMOTION, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      if (data?.promotion) {
        const p = data.promotion
        setDraft({
          promotionType: p.promotionType,
          name: p.name,
          description: p.description || '',
          triggerType: p.triggerType,
          couponCode: p.couponCode || '',
          targeting: p.targeting,
          channel: p.channel,
          isCatering: !!p.isCatering,
          isStackable: p.isStackable,
          priority: p.priority,
          maxRedemptions: p.maxRedemptions || '',
          maxPerCustomer: p.maxPerCustomer || '',
          maxDiscountAmount: p.maxDiscountAmount || '',
          startAtUtc: p.startAtUtc ? new Date(p.startAtUtc).toISOString().slice(0, 16) : '',
          endAtUtc: p.endAtUtc ? new Date(p.endAtUtc).toISOString().slice(0, 16) : '',
          dayOfWeek: p.dayOfWeek || [],
          timeWindowStart: p.timeWindowStart || '',
          timeWindowEnd: p.timeWindowEnd || '',
          stopIds: (p.stopIds || []).map(String),
          conditions: (p.conditions || []).map(c => ({ conditionType: c.conditionType, params: c.params })),
          rewards: (p.rewards || []).map(r => ({ rewardType: r.rewardType, params: r.params })),
        })
        setSavedId(p._id)
      }
    },
  })

  // A food truck can limit a deal to some of its stops.
  const { data: profileData } = useQuery(GET_RESTAURANT_PROFILE, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'cache-first',
  })
  const isTruck = profileData?.restaurant?.isMobile === true
  const { data: stopsData } = useQuery(GET_STOPS, {
    variables: { restaurantId, includePast: true },
    skip: !isTruck,
    fetchPolicy: 'cache-and-network',
  })
  const { data: trucksData } = useQuery(GET_TRUCKS, {
    variables: { restaurantId },
    skip: !isTruck,
    fetchPolicy: 'cache-first',
  })
  // Stops still on the schedule (repeating, or a one-off today or later), plus
  // any already picked, so an older pick can still be seen and removed.
  const stopOptions = useMemo(() => {
    const picked = draft.stopIds || []
    const trucks = trucksData?.restaurantTrucks || []
    const truckName = new Map(trucks.map(t => [String(t._id), t.name]))
    const today = new Date().toISOString().slice(0, 10)
    const options = (stopsData?.restaurantStops || [])
      .filter(s => s.recurrence === 'WEEKLY' || s.recurrence === 'MONTHLY' ||
        (s.stopDate || '') >= today || picked.includes(String(s._id)))
      .map(s => ({
        id: String(s._id),
        label: s.label || s.address,
        detail: [
          trucks.length > 1 ? truckName.get(String(s.truckId)) : null,
          s.scheduleLabel,
          s.windowLabel,
        ].filter(Boolean).join(' · '),
      }))
    // A picked stop that's gone from the schedule.
    picked.filter(id => !options.some(o => o.id === id))
      .forEach(id => options.push({ id, label: 'A removed stop', detail: 'No longer on the schedule' }))
    return options
  }, [stopsData, trucksData, draft.stopIds])

  const [mutateCreate, { loading: creating }] = useMutation(CREATE_PROMOTION)
  const [mutateUpdate, { loading: updating }] = useMutation(UPDATE_PROMOTION)
  const [mutatePublish, { loading: publishing }] = useMutation(PUBLISH_PROMOTION)

  const buildInput = () => ({
    restaurantId: restaurantId || null,
    promotionType: draft.promotionType,
    name: draft.name,
    description: draft.description || null,
    triggerType: draft.triggerType,
    couponCode: draft.triggerType === 'COUPON_CODE' ? draft.couponCode : null,
    targeting: draft.targeting,
    channel: draft.channel,
    isCatering: !!draft.isCatering,
    isStackable: draft.isStackable,
    priority: parseInt(draft.priority, 10) || 0,
    maxRedemptions: draft.maxRedemptions ? parseInt(draft.maxRedemptions, 10) : null,
    maxPerCustomer: draft.maxPerCustomer ? parseInt(draft.maxPerCustomer, 10) : null,
    maxDiscountAmount: draft.maxDiscountAmount ? parseFloat(draft.maxDiscountAmount) : null,
    startAtUtc: draft.startAtUtc ? `${draft.startAtUtc}:00Z` : new Date().toISOString(),
    endAtUtc: draft.endAtUtc ? `${draft.endAtUtc}:00Z` : null,
    dayOfWeek: draft.dayOfWeek?.length ? draft.dayOfWeek : null,
    timeWindowStart: draft.timeWindowStart || null,
    timeWindowEnd: draft.timeWindowEnd || null,
    // Only a food truck sends this ([] = every stop); left out, it stays as it was.
    ...(isTruck ? { stopIds: draft.stopIds || [] } : {}),
    conditions: draft.conditions.map(c => ({
      conditionType: c.conditionType,
      params: typeof c.params === 'string' ? JSON.parse(c.params) : c.params,
    })),
    rewards: draft.rewards.map(r => ({
      rewardType: r.rewardType,
      params: typeof r.params === 'string' ? JSON.parse(r.params) : r.params,
    })),
  })

  const saveDraft = async() => {
    setError('')
    try {
      const input = buildInput()
      if (savedId) {
        await mutateUpdate({ variables: { id: savedId, input } })
        setSuccess('Draft saved')
      } else {
        const res = await mutateCreate({ variables: { input } })
        const newId = res.data?.createPromotion?._id
        setSavedId(newId)
        setSuccess('Draft saved')
      }
    } catch (e) {
      setError(e.message)
    }
  }

  const handlePublish = async() => {
    setError('')
    try {
      await saveDraft()
      if (savedId) {
        await mutatePublish({ variables: { id: savedId } })
        setSuccess('Promotion published!')
        setTimeout(() => history.push(restaurantId ? '/admin/promotions' : '/super_admin/promotions'), 1200)
      }
    } catch (e) {
      setError(e.message)
    }
  }

  const validate = () => {
    if (step === 0 && !draft.promotionType) return 'Please choose a promotion type.'
    if (step === 1 && !draft.name.trim()) return 'Promotion name is required.'
    if (step === 1 && draft.triggerType === 'COUPON_CODE' && !draft.couponCode.trim()) return 'Coupon code is required.'
    if (step === 3 && !draft.rewards.length) return 'Add at least one reward.'
    return ''
  }

  const next = () => {
    const err = validate()
    if (err) { setError(err); return }
    setError('')
    setStep(s => s + 1)
  }

  const back = () => { setError(''); setStep(s => s - 1) }

  if (loadingPromo) return <ThemeProvider theme={promotionsTheme}><Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box></ThemeProvider>

  const isLastStep = step === STEPS.length - 1
  const isSaving = creating || updating || publishing

  return (
    <ThemeProvider theme={promotionsTheme}>
      <Box sx={PAGE}>
        <Box sx={{
          maxWidth: 900, mx: 'auto', boxSizing: 'content-box',
          px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: 12,
          display: 'flex', flexDirection: 'column', gap: 3.5
        }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: PM.text }}>
            {savedId ? 'Edit Promotion' : 'Create Promotion'}
          </Typography>

          <Stepper activeStep={step} alternativeLabel sx={STEPPER}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel StepIconComponent={StepDot}>{label}</StepLabel></Step>
            ))}
          </Stepper>

          <Paper elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${PM.border}`, boxShadow: 'none', bgcolor: '#FFFFFF', overflow: 'hidden' }}>
            <Box sx={{ p: { xs: '24px 20px', sm: '32px 32px 28px' }, display: 'flex', flexDirection: 'column', gap: '22px' }}>
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              {step === 0 && <StepType draft={draft} setDraft={setDraft} />}
              {step === 1 && <StepTrigger draft={draft} setDraft={setDraft} />}
              {step === 2 && <StepConditions draft={draft} setDraft={setDraft} />}
              {step === 3 && <StepReward draft={draft} setDraft={setDraft} />}
              {step === 4 && <StepLimits draft={draft} setDraft={setDraft} isTruck={isTruck} stopOptions={stopOptions} />}
              {step === 5 && <StepPreview draft={draft} promotionId={savedId} />}
              {step === 6 && <StepPublish draft={draft} isTruck={isTruck} stopOptions={stopOptions} />}
            </Box>

            {/* Footer: Back on the left, Save Draft + Next / Publish Now on the right */}
            <Box sx={{
              borderTop: `1px solid ${PM.border}`, px: { xs: 2.5, sm: 4 }, py: '18px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'
            }}>
              <Button onClick={back} disabled={step === 0} startIcon={<ChevronLeftRoundedIcon />}
                sx={{ textTransform: 'none', fontWeight: 700, pl: 1, pr: 1.5, '& .MuiButton-startIcon': { mr: 0.5 } }}>Back</Button>
              <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Button onClick={saveDraft} variant="outlined" disabled={isSaving}
                  sx={{ textTransform: 'none', borderRadius: '10px' }}>
                  Save Draft
                </Button>
                {isLastStep ? (
                  <Button onClick={handlePublish} variant="contained" disabled={isSaving}
                    sx={{ textTransform: 'none', borderRadius: '10px', px: 3 }}>
                    {publishing ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    Publish Now
                  </Button>
                ) : (
                  <Button onClick={next} variant="contained" endIcon={<ChevronRightRoundedIcon />}
                    sx={{ textTransform: 'none', borderRadius: '10px', pl: 3, pr: 2.25, '& .MuiButton-endIcon': { ml: 0.5 } }}>
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
        <PromotionHelp currentStep={step} />
      </Box>
    </ThemeProvider>
  )
}

export default PromotionWizard
